// Angular
import { Injectable } from '@angular/core';

// Rxjs
import { Observable, Subject } from 'rxjs';

// Models
import { BrandOption, BrandOptionHelper } from '../../models/b2b/brandOption';
import { Product, ProductHelper } from '../../models/b2b/product';
import { ProductTaxInfo, ProductTaxInfoHelper } from '../../models/b2b/productTaxInfo';
import { Search, SearchHelper } from '../../models/b2b/search';
import { SearchFeatureOption, SearchFeatureOptionsHelper } from '../../models/b2b/searchFeatureOption';
import { SearchIncrement } from '../../models/b2b/searchIncrement';
import { SearchParams, SearchParamsHelper } from '../../models/b2b/searchParams';
import { SearchSettings } from '../../models/b2b/searchSettings';
import { User, UserType, UserHelper } from '../../models/b2b/user';

// Services
import { ApiService } from '../api/api.service';
import { SearchFeatureChecks, SearchFeatureChecksHelper } from '../../models/b2b/searchFeatureChecks';
import { SearchType, searchTypeToAPI } from '../../models/b2b/searchType';
import { of } from 'rxjs';
import { Subscriber } from '../../lib/helpers/subscriber';
import { AppState } from '../../../store';
import { Store } from '@ngrx/store';
import { map } from 'rxjs/operators';
import * as _ from 'lodash';
import { validateApiResponse } from 'app/shared/lib/custom-rxjs-operators/api-response.operator';

@Injectable({ providedIn: 'root' })
export class SoledadSearchService extends Subscriber {
    private addressCode: number = null;
    private almacenCodigo: string = null;
    private settings: SearchSettings = null;
    public productsSearchMobileOpen = new Subject<boolean>();
    public searchBrandsSubject = new Subject<BrandOption[]>();

    constructor(
        private api: ApiService,
        private store: Store<AppState>
    ) {
        super();
        this.addSubscription(
            this.store
                .select((state) => state.user.data)
                .subscribe((user) => {
                    const address = UserHelper.getSelectedAddress(user);
                    this.addressCode = address ? address.codigo : null;
                    this.almacenCodigo = address ? address.almacenCodigo : null;
                })
        );
        this.addSubscription(
            this.store
                .select((state) => state.filterTop.settings)
                .subscribe((settings) => {
                    this.settings = settings;
                })
        );
    }

    /**
     * Search products
     *
     * @param      search         The search
     * @param      searchParams   The search parameters
     *
     * @return     { description_of_the_return_value }
     */

    /**
     * Search products
     * @param {SearchParams} searchParams Search params
     */
    search(user: User, settings: SearchSettings, searchParams: SearchParams): Observable<Search> {
        // console.log('SEARCH', { user }, { searchParams }, { settings });

        // Don't search
        if (!user || !settings) {
            return of(null);
        }

        searchParams = _.cloneDeep(searchParams);

        // Params to api
        const params = {};
        // Delivery

        // Get address
        let address = UserHelper.getUserDefaultAddress(user);
        //let address = user.addresses.find(aux => aux.defecto === true);
        if (!address) {
            address = user.addresses.length > 0 ? user.addresses[user.addresses.length - 1] : null;
        }
        params['DIRECCIONENVIO'] = address.codigo;
        params['ALMACEN'] = address.almacenCodigo;

        // Settings
        params['REGISTROS'] = settings.numArticulos;
        params['ICCVSUPERIORES'] = searchParams.iccvsuperiores ? true : false;

        // Search
        params['BUSQUEDA'] = searchParams.busqueda ? searchParams.busqueda : '';
        params['BUSQUEDA2'] = searchParams.busqueda2 ? searchParams.busqueda2 : '';
        if (searchParams.indiceCarga) {
            params['INDICECARGA'] = searchParams.indiceCarga;
        }
        if (searchParams.indiceCarga2) {
            params['INDICECARGA2'] = searchParams.indiceCarga2;
        }
        if (searchParams.codigoVelocidad) {
            params['CODIGOVELOCIDAD'] = searchParams.codigoVelocidad;
        }
        if (searchParams.codigoVelocidad2) {
            params['CODIGOVELOCIDAD2'] = searchParams.codigoVelocidad2;
        }

        // Filter Top
        const stockEnabled = !!searchParams.stock || !!settings.predetConStock;
        params['STOCK'] = stockEnabled ? 1 : 0;
        params['STOCK_MINIMO'] = stockEnabled ? searchParams.stock_minimo : 0;

        // Extra params
        params['INVIERNO'] = searchParams.invierno ? 1 : 0;
        params['ALLSEASONS'] = searchParams.allseasons ? 1 : 0;
        params['VERANO'] = searchParams.verano ? 1 : 0;
        params['EMPAREJAR'] = searchParams.busqueda && searchParams.busqueda2 ? 1 : 0;
        // params['RUNFLAT'] = searchParams.runflat ? true : false;
        params['RUNFLAT'] = searchParams.runflat ? 1 : 0;
        params['NORUNFLAT'] = searchParams.withoutRunflat ? true : false;
        params['CODIGOMS'] = searchParams.codigoms ? 1 : 0;

        searchParams.sealInside ? (params['SEALINSIDE'] = 1) : 0;
        searchParams.protector ? (params['PROTECTOR'] = 1) : 0;
        searchParams.ice ? (params['HIELO'] = true) : 0;
        searchParams.snow ? (params['NIEVE'] = true) : 0;
        searchParams.electric ? (params['ELECTRICO '] = true) : 0;

        if (searchParams.semiSlick) {
            params['SEMI_SLICK'] = 1;
        }

        params['OFERTASESPECIALES'] = searchParams.specialOffers ? true : false;
        if (searchParams.semiSlick) {
            params['SEMI_SLICK'] = 1;
        }

        params['IMAGENES'] = 1;
        params['OBSOLETOS'] = searchParams.obsoletos;
        searchParams.homologaciones ? (params['HOMOLOGACIONES'] = searchParams.homologaciones) : '';

        params['EQUIVALENCIAS'] = searchParams.equivalences ? 1 : 0;

        searchParams.ordenBusqueda = settings.ordenBusqueda;
        params['ORDEN'] = searchParams.ordenBusqueda;

        if (searchParams.protector) {
            params['PROTECTOR'] = searchParams.protector ? true : false;
        }
        // params['PAGINA'] = (searchParams.pagina - 1);

        searchParams.numRuedas = settings.numRuedas;
        if (searchParams.numRuedas > 0) {
            params['CANTIDAD'] = searchParams.numRuedas;
        }
        if (searchParams.tipoVehiculo) {
            params['TIPOVEHICULO'] = searchParams.tipoVehiculo.trim();
        }
        if (searchParams.tipoArticulo) {
            params['TIPOARTICULO'] = searchParams.tipoArticulo.trim();
        }
        if (searchParams.gama) {
            params['GAMA'] = searchParams.gama;
        }
        if (searchParams.posicion) {
            params['POSICION'] = searchParams.posicion;
        }
        if (searchParams.uso) {
            params['USO'] = searchParams.uso;
        }
        if (searchParams.estructura) {
            params['ESTRUCTURA'] = searchParams.estructura;
        }
        if (searchParams.categoria) {
            params['CATEGORIA'] = searchParams.categoria;
        }
        if (searchParams.dipos) {
            params['DIPOS'] = searchParams.dipos;
        }
        if (searchParams.diesag) {
            params['DIESAG'] = searchParams.diesag;
        }
        if (searchParams.diuso) {
            params['DIUSO'] = searchParams.diuso;
        }

        // Stock
        if (searchParams.inmediata) {
            params['TIPO_ENTREGA'] = 'INMEDIATA';
        }

        // Brand
        params['MARCA'] = searchParams.marca ? searchParams.marca : '';

        // User
        if (user.tipoUsuario === 'COMERCIAL') {
            params['CLIENTE'] = user.clienteCodigo;
        }

        // Init Searching
        const search = SearchHelper.create({
            total: 0,
            products: []
        });

        // Increment
        if (settings.incremento_tipo) {
            searchParams.increment.type = settings.incremento_tipo;
        }
        if (settings.incremento_cantidad) {
            searchParams.increment.value = settings.incremento_cantidad;
        }

        // Search
        return this.getResults(params, searchParams.increment).pipe(
            map((products) => {
                // Search construction
                const searchResult = SearchHelper.create();
                searchResult.total = products.length;
                searchResult.products = products;
                searchResult.params = searchParams;

                // console.log('HTML_ArticulosBusqueda', searchResult);
                return searchResult;
            })
        );
    }

    searchByCodes(
        user: User,
        settings: SearchSettings,
        codes: string[],
        increment: SearchIncrement
    ): Observable<Product[]> {
        // Params to api
        const params = {};

        // Delivery
        params['DIRECCIONENVIO'] = 0;
        params['ALMACEN'] = 1;
        params['REGISTROS'] = settings.numArticulos;
        params['STOCK'] = 0;
        params['BUSQUEDA'] = codes.join(',');

        if (user.type === UserType.Cliente) {
            params['CLIENTE'] = user.clienteCodigo;
        }

        return this.getResults(params, increment).pipe(
            map((products) => {
                return products;
            })
        );
    }

    getResults(params, increment: SearchIncrement): Observable<Product[]> {
        params = _.omitBy(params, (v) => v == null);
        return this.api.callSoledad('b2bNew.dbo.HTML_ArticulosBusqueda', params).pipe(
            validateApiResponse('Error obteniendo productos' + '. Params: ' + JSON.stringify(params)),
            map((products: any[]) => {
                const transformedProducts = [];
                products.forEach((product: any, index: number) => {
                    const transformedProduct = ProductHelper.transformFromApi(product, this.api.environment, index);
                    ProductHelper.setIncrement(transformedProduct, increment);
                    transformedProducts.push(transformedProduct);
                });
                return transformedProducts;
            })
        );
    }

    /**
     * Get Brands
     * (update observable brand )
     * @param {string} type - Type from const SearchVehiclesType
     */
    getBrands(type: string): Observable<BrandOption[]> {
        const params = [];
        params['TIPOMARCAS'] = type;

        // Call API Brands
        return this.api.callSoledad('b2bNew.dbo.HTML_MarcasBusqueda', params).pipe(
            validateApiResponse('Error obteniendo marcas' + '. Params: ' + JSON.stringify(params)),
            map((brands: any[]) => {
                return brands.map((brand: any) => BrandOptionHelper.transformFromApi(brand));
            })
        );
    }

    /**
     * Load search feature options
     * @param {string} type - Type from const SearchVehiclesType
     */
    getFeatureOptions(
        type: string,
        feature: string,
        searchParams: SearchParams = null
    ): Observable<SearchFeatureOption[]> {
        let params = {};

        if (type == 'OTROSPRODUCTOS' && feature === 'CATEGORIA') {
            params = undefined;
            params = [];
        } else {
            params = SearchParamsHelper.toAPIChecks(searchParams);
        }

        if (type === '4X4' && feature === 'USO') {
            params['USO'] = null;
        }
        if (feature === 'HOMOLOGACION') {
            params['HOMOLOGACIONES'] = null;
        }

        // Filter Top
        params['STOCK'] = (!!searchParams.stock || !!this.settings?.predetConStock) ? 1 : 0;

        params['DIRECCIONENVIO'] = this.addressCode;
        params['ALMACEN'] = this.almacenCodigo;

        params['TIPOBUSQUEDA'] = type;
        params['CARACTERISTICA'] = feature;

        params = _.omitBy(params, (v) => v == null);

        // Call API Feature Options
        return this.api.callSoledad('b2bNew.dbo.HTML_OpcionesCaracteristicas', params).pipe(
            validateApiResponse('Error obteniendo opciones de características' + '. Params: ' + JSON.stringify(params)),
            map((options: any[]) => {
                return options.map((option: any) => SearchFeatureOptionsHelper.transformFromApi(option));
            })
        );
    }

    /**
     * Load search feature checks
     */
    getFeatureChecks(type: SearchType, searchParams: SearchParams): Observable<SearchFeatureChecks> {
        let params = SearchParamsHelper.toAPIChecks(searchParams);

        // Filter Top
        params['STOCK'] = (!!searchParams.stock || !!this.settings?.predetConStock) ? 1 : 0;

        params['ALMACEN'] = this.almacenCodigo;

        params['TIPOBUSQUEDA'] = searchTypeToAPI.get(type);
        params['CARACTERISTICA'] = 'CHECK';
        params['DIRECCIONENVIO'] = this.addressCode;

        params = _.omitBy(params, (v) => v == null);

        // Call API Feature Options
        return this.api.callSoledad('b2bNew.dbo.HTML_OpcionesCaracteristicas', params).pipe(
            validateApiResponse('Error obteniendo opciones de características' + '. Params: ' + JSON.stringify(params)),
            map((res) => {
                return SearchFeatureChecksHelper.fromAPI(res[0]);
            })
        );
    }

    initOpcionesBusqueda(params) {
        this.api.callSoledad('b2bNew.dbo.HTML_TipoVehiculosBusqueda', params).subscribe((res) => {});
    }

    initMedidasMasBuscadas(user: User): Observable<string[]> {
        if (user != null) {
            const params = [];
            params['CLIENTE'] = user.clienteCodigo;

            return this.api.callSoledad('b2bNew.dbo.HTML_TipoVehiculosBusqueda', params).pipe(
                map((res) => {
                    if (!res) {
                        return of([]);
                    }

                    return res.value.Rows.map((row) => row['BUSQUEDA']);
                })
            );
        }
    }

    getPrecioTotal(user: User, productId, price, quantity): Observable<ProductTaxInfo> {
        if (!user) {
            return of(null);
        }

        const params = [];

        params['CLIENTE'] = user.clienteCodigo;
        params['ARTICULO'] = productId;
        params['PRECIO'] = price;
        params['UNIDADES'] = quantity;

        return this.api.callSoledad('b2bNew.dbo.HTML_ArticulosPrecioTotal', params).pipe(
            validateApiResponse('Error obteniendo precio total' + '. Params: ' + JSON.stringify(params)),
            map((res) => {
                return ProductTaxInfoHelper.transformFromApi(res.Rows, price);
            })
        );
    }

    doSendSearchNoResult(settings): Observable<string> {
        // console.log('doSendSearchNoResult', {settings});

        const params = [];
        params['CONSULTA'] = settings.keyword;
        params['PERSONA_CONTACTO'] = settings.contact;
        params['TELEFONO'] = settings.phone;
        params['EMAIL'] = settings.email;
        params['DESCRIPCION'] = settings.description;

        return this.api.callSoledad('b2bNew.dbo.HTML_BusquedaSinResultado', params).pipe(
            validateApiResponse('Error obteniendo búsqueda sin resultado' + '. Params: ' + JSON.stringify(params)),
            map((data) => {
                // console.log('HTML_BusquedaSinResultado', {data});
                return data;
            })
        );
    }

    getSearchType(user: User, searchParams: SearchParams): Observable<any> {
        const params = [];
        params['BUSQUEDA'] = searchParams.busqueda ? searchParams.busqueda : '';
        params['CLIENTE'] = user.clienteCodigo;
        params['devolverTipos'] = true;

        return this.api.callSoledad('b2bNew.dbo.HTML_ArticulosBusquedaMedida', params).pipe(
            validateApiResponse('Error HTML_ArticulosBusquedaMedida' + '. Params: ' + JSON.stringify(params)),
            map((data) => {
                return data;
            })
        );
    }
}
